<template>
    <section class="grid grid-cols-2 gap-x-10">
        <div class="flex justify-between col-span-2 border-b border-sepBorder dark:border-dark.sepBorder pb-3 mb-4">
            <h2 class="font-bold text-2xl">{{ $t('assign_employee') }}</h2>
            <div class="text-right">
                <h2 class="font-semibold"> {{modalField.den}} - {{modalField.date}} ({{$t('shift_settings[6]')}}: {{formatTime(modalField.date + ' ' + helpFrom,"HH:mm")}} - {{formatTime(modalField.date + ' ' + helpTo,"HH:mm")}})</h2>
                <h2>{{ $t('creator') }}: {{modalField.modelName}}</h2>
                <h2 class="text-sm font-semibold text-center" v-if="modalField.assignedBy">Assigned by: {{modalField.assignedBy}} {{modalField.assignedAt}}</h2>
            </div>
        </div>
        <div>
            <h3 class="text-xl font-bold leading-relaxed">Quick Shift Assignment</h3>
            <p>Set up individual shifts with ease using the 'Assign an Employee' function. Choose times, toggle between start and end times with precision, and opt for single or multiple shift assignments with the 'Save for one model' or 'Save for the team' buttons.</p>

            <h3 class="text-xl font-bold mt-5 leading-relaxed">Team Scheduling Made Simple</h3>
            <p>'Save for the team' enables you to assign an employee to consistent shifts across multiple schedules, streamlining the planning process for recurring work patterns.</p>
        </div>
        <section v-if="loading">
            <div class="h-full sm:w-full flex items-center justify-center">
                <font-awesome-icon icon="fas fa-spinner" class="animate-spin" />
            </div>
        </section>
        <section v-else>
            <MazSelect
                v-model="modalField.empl"
                :options="[{ value: '0', label: 'Choose employee' }, ...employeeList.map((v, i) => ({ value: v.id, label: v.name }))]"
                :label="$t('name')"
            />
            <div class="flex items-center justify-between my-2 gap-3">
                <div>
                    <MazPicker
                        v-model="shiftTime[0]"
                        format="HH:mm"
                        :label="$t('time_from')"
                        only-time
                        auto-close
                    />
                </div>
                <div>
                    <MazPicker
                        v-model="shiftTime[1]"
                        format="HH:mm"
                        :label="$t('time_to')"
                        only-time
                        auto-close
                    />
                </div>
                <div class="leading-5">
                    <label class="text-xs text-gray-500 whitespace-nowrap">{{ $t('shift_duration') }}:</label><br />
                    <span class="font-semibold whitespace-nowrap">{{ hours }} {{ $t('hours') }}</span>
                </div>
            </div>

            <MazCheckbox 
                v-model="addNote"
                size="xs"
                class="mt-2">
                {{ $t('add_a_note') }}
            </MazCheckbox>

            <MazTextarea
                v-if="addNote"
                class="mt-2"
                :placeholder="$t('note')"
                v-model="modalField.note"
            />

            <div class="cursor-pointer w-max mt-2 mb-2 leading-none">
                <MazCheckbox v-model="modalField.salary" size="xs" class="mb-1">
                    {{ $t('give_salary') }}
                </MazCheckbox>
                <small class="font-light text-gray-500"><br />(* {{ $t('if_not_give_salary') }})</small>
            </div>

            <div v-if="!modalField.shiftID && modalField.split" class="bg-red-400 text-white p-2 px-3 rounded text-center font-bold my-2 mt-4">
                {{ $t('change_time_notice') }}
            </div>
            <hr class="my-3 border-sepBorder dark:border-dark.sepBorder" />

            <div class="text-decline py-2" v-if="errorM">{{ errorM }}</div>
            <section class="grid grid-cols-2 gap-2 mt-3">
                <MazBtn @click="forAll = false, saveShift(modalField.teamID)" outline>
                    {{ $t('save_for_this_creator') }}
                </MazBtn>
                <MazBtn @click="forAll = true, saveShift(modalField.teamID)">
                    {{ $t('save_for_the_team') }}
                </MazBtn>
            </section>
        </section>
    </section>
</template>

<script>
    export default {
        props: ['modalField','employeeList','helpFrom','errorMessage','helpTo','timeZone','myself'],
        emits: ['result'],
        data() {
            return {
                mounted: false,
                loading: false,
                addNote: false,
                shiftTime: [],
                errorM: null
            }
        },
        computed: {
            hours() {
                // Parse start and end times from shiftTime array
                var start = this.$moment(this.shiftTime[0], "HH:mm:ss");
                var end = this.$moment(this.shiftTime[1], "HH:mm:ss");

                // Extract hour and minute parts
                var startHours = start.hour();
                var endHours = end.hour();
                var startMinutes = start.minute();
                var endMinutes = end.minute();

                var totalHours;

                if (startHours > endHours) {
                    // Shift crosses midnight
                    totalHours = (24 - startHours) + endHours;
                } else if (startHours < endHours) {
                    // Same day, no crossing
                    totalHours = endHours - startHours;
                } else {
                    // Start hour equals end hour
                    if (startMinutes >= endMinutes) {
                        totalHours = 1; // Enforce minimum 1 hour if times are equal or invalid
                    } else {
                        totalHours = 0; // Base hours 0, will adjust with minutes
                    }
                }

                // Adjust for minutes in 5-minute increments
                totalHours += endMinutes / 60; // Add end minutes as fractional hours
                totalHours -= startMinutes / 60; // Subtract start minutes as fractional hours

                // Ensure minimum duration of 1 hour
                if (totalHours < 1) {
                    totalHours = 1;
                }

                return totalHours.toFixed(2);
            }
        },
        methods: {
            determinZoneDST(original) {
                var tzone = 0;
                if (this.$moment().isDST()) {
                    if (this.$moment(original,"YYYY-MM-DD HH:mm:ss").isDST()) {
                        tzone = parseInt(this.timeZone)
                    } else {
                        tzone = (parseInt(this.timeZone)/60-1)*60
                    }
                } else {
                    if (this.$moment(original,"YYYY-MM-DD HH:mm:ss").isDST()) {
                        tzone = (parseInt(this.timeZone)/60+1)*60
                    } else {
                        tzone = parseInt(this.timeZone)
                    }
                }
                return tzone;
            },
            formatTime(original,finalFormat) {
                if (this.$moment().isDST()) {
                    if (this.myself && this.myself.companyTz) {
                        if (parseInt(this.timeZone)<0) {
                            return this.$moment(original,"YYYY-MM-DD HH:mm:ss").subtract((parseInt(this.timeZone)/60+1)*60, 'minutes').format(finalFormat);
                        } else {
                            return this.$moment(original,"YYYY-MM-DD HH:mm:ss").add((parseInt(this.timeZone)/60-1)*60, 'minutes').format(finalFormat);
                        }
                    } else { // MSB branch ONLY -> IN SUMMER
                        var tzone = this.determinZoneDST(original)
                        if (parseInt(this.timeZone)<0) {
                            return this.$moment(original,"YYYY-MM-DD HH:mm:ss").subtract(tzone, 'minutes').format(finalFormat);
                        } else {
                            return this.$moment(original,"YYYY-MM-DD HH:mm:ss").add(tzone, 'minutes').format(finalFormat);
                        }
                    }
                } else {
                    if (parseInt(this.timeZone)<0) {
                        return this.$moment(original,"YYYY-MM-DD HH:mm:ss").subtract(parseInt(this.timeZone), 'minutes').format(finalFormat);
                    } else {
                        return this.$moment(original,"YYYY-MM-DD HH:mm:ss").add(parseInt(this.timeZone), 'minutes').format(finalFormat);
                    }
                }
            },
            formatTimeBack(original,finalFormat) {
                if (this.$moment().isDST()) {
                    if (this.myself && this.myself.companyTz) {
                        if (parseInt(this.timeZone)<0) {
                            return this.$moment(original,"YYYY-MM-DD HH:mm:ss").add((parseInt(this.timeZone)/60+1)*60, 'minutes').format(finalFormat);
                        } else {
                            return this.$moment(original,"YYYY-MM-DD HH:mm:ss").subtract((parseInt(this.timeZone)/60-1)*60, 'minutes').format(finalFormat);
                        }
                    } else { // MSB branch ONLY -> IN SUMMER
                        var tzone = this.determinZoneDST(original)
                        if (parseInt(this.timeZone)<0) {
                            return this.$moment(original,"YYYY-MM-DD HH:mm:ss").add(tzone, 'minutes').format(finalFormat);
                        } else {
                            return this.$moment(original,"YYYY-MM-DD HH:mm:ss").subtract(tzone, 'minutes').format(finalFormat);
                        }
                    }
                } else {
                    if (parseInt(this.timeZone)<0) {
                        return this.$moment(original,"YYYY-MM-DD HH:mm:ss").add(parseInt(this.timeZone), 'minutes').format(finalFormat);
                    } else {
                        return this.$moment(original,"YYYY-MM-DD HH:mm:ss").subtract(parseInt(this.timeZone), 'minutes').format(finalFormat);
                    }
                }
            },
            saveShift(teamID) {
                this.loading = true;
                if (this.modalField.empl == 0) {
                    this.loading = false;
                    this.errorM = this.$t('assign_employee');
                    return;
                } else {
                    this.errorM = null;
                }

                this.modalField.timeFrom = this.formatTimeBack(this.modalField.date + ' ' + this.shiftTime[0], "HH:mm:ss");
                this.modalField.timeTo = this.formatTimeBack(this.modalField.date + ' ' + this.shiftTime[1], "HH:mm:ss");
                this.modalField.hours = this.hours;

                this.$axios.post("shifts/", {
                    action: "saveShiftTimeTable",
                    modelObject: this.modalField,
                    forAll: this.forAll,
                    summer: this.$moment(this.modalField.date,'DD.MM.YYYY').isDST(),
                    userToken: this.$cookies.get('userToken')
                }).then((result) => {
                    var groupIndex = this.modalField.indexes['gIndex']; // index of the 1 layer - group
                    var modelIndex = this.modalField.indexes['ind']; // index of the 2 layer - model
                    var dataIndex = this.modalField.indexes['index']; 
                    var shiftIndex = this.modalField.indexes['i']; //id of the shift of the day
                    if (result.data && result.data.success == true) {
                        this.$emit('result', {groupIndex, modelIndex, dataIndex, shiftIndex, result:result.data,teamID:teamID});
                    } else {
                        alert("Server error, shift not added. Please refresh the page");
                    }
                }, (error) => {
                    console.log(error);
                }).finally(() => this.loading = false);
                this.showModalkey = false;
            },
        },
        mounted() {
            this.mounted = true;
            //wanted format [ "02:00:00", "10:00:00" ], this.modalField.timeFrom and this.modalField.timeTo are in format "HH" this.modalField.timeFromMinutes are integer 0-59 and this.modalField.timeToMinutes are integer 0-59
            this.shiftTime = [
                this.formatTime(this.modalField.date + ' ' + this.helpFrom, "HH:mm:ss"),
                this.formatTime(this.modalField.date + ' ' + this.helpTo, "HH:mm:ss")
            ];
        }
    }
</script>